import React, { FC, useMemo, useEffect } from 'react';
import { MONETATE_DEFAULT_VARIANT } from 'constants/monetate';
import { useWtrDispatch, useWtrSelector } from 'redux/hooks';
import { getAllDecisions, getAllPayloads } from 'redux/modules/experiments/selectors';
import { trackExperimentView } from 'redux/modules/experiments/actions/track-experiment-view';
import { MonetatePayload } from 'redux/modules/experiments/actions/types';
import { DOM_PURIFY_ALLOW_LIST } from 'constants/omnichannelExperimentation';
import { useLocation } from 'react-router-dom';
import DOMPurify from 'isomorphic-dompurify';
import { log } from 'utils/error-logging';
import { isPageWorkersExperimentPath } from '../utils';

export type OmnichannelElementsProps = {
  id: string;
};

type OmnichannelElementPayload = {
  type: 'OmnichannelElement';
  hook: string;
  html: string;
};

const OmnichannelElements: FC<OmnichannelElementsProps> = ({ id }) => {
  const { pathname } = useLocation();
  const dispatch = useWtrDispatch();
  // @ts-expect-error experiments state is not typed
  const decisions = useWtrSelector<Record<string, string>>(getAllDecisions);
  // @ts-expect-error experiments state is not typed
  const payloads = useWtrSelector<Record<string, MonetatePayload>>(getAllPayloads);

  const elements = useMemo(() => {
    if (!payloads) return [];

    const filteredEntries = Object.entries(payloads).filter(([, payload]) => {
      return (
        typeof payload === 'object' &&
        typeof payload?.html === 'string' &&
        payload?.type === 'OmnichannelElement' &&
        payload?.hook === id
      );
    });

    return filteredEntries.map(([experimentId, payload]) => ({
      experimentId,
      payload: payload as OmnichannelElementPayload,
    }));
  }, [id, payloads]);

  useEffect(() => {
    // Only allow omnichannel experimentation on offers and recipes pages
    if (!isPageWorkersExperimentPath(pathname)) {
      return;
    }

    elements.forEach(({ experimentId }) => {
      dispatch(
        trackExperimentView({
          id: experimentId,
          variant: decisions?.[experimentId] || MONETATE_DEFAULT_VARIANT,
          trackAll: false,
        }),
      );
    });
  }, [elements, decisions, dispatch, pathname]);

  const sanitisedElements = useMemo(() => {
    return elements.map(({ experimentId, payload }) => {
      let sanitisedHtml = '';

      try {
        sanitisedHtml = DOMPurify.sanitize(payload.html, DOM_PURIFY_ALLOW_LIST);
      } catch (error) {
        log(`Error sanitising HTML for experiment "${experimentId}" at hook "${id}": ${error}`);
      }

      return {
        experimentId,
        sanitisedHtml,
      };
    });
  }, [elements, id]);

  // Only allow omnichannel experimentation on offers and recipes pages
  if (!isPageWorkersExperimentPath(pathname)) {
    return null;
  }

  return (
    <>
      {sanitisedElements.map(({ experimentId, sanitisedHtml }) => {
        return (
          <div
            key={experimentId}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: sanitisedHtml,
            }}
          />
        );
      })}
    </>
  );
};

export default OmnichannelElements;
